if (!customElements.get('bm-days-planner')) {
  class BmDaysPlanner extends HTMLElement {
    static quantityForDays(target,days,minimum=1,increment=1,maximum=6) {
      if (!(days > 0) || !(target > 0)) return null;
      const required = Math.ceil(target / days);
      const quantity = minimum + Math.max(0,Math.ceil((required - minimum) / increment)) * increment;
      return quantity <= maximum ? quantity : null;
    }
    connectedCallback() {
      customElements.whenDefined('bm-quantity-offers').then(() => queueMicrotask(() => this.initialize()));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }
    initialize() {
      if (!this.isConnected || this.abort && !this.abort.signal.aborted) return;
      this.offers = this.closest('bm-quantity-offers');
      this.form = this.closest('form');
      this.quantity = this.form?.querySelector('[data-bm-qty-input],[name="quantity"]');
      this.slider = this.querySelector('[data-bm-days-slider]');
      this.packDays = Number(this.dataset.packDays);
      if (!this.offers?.variants || !this.quantity || !this.slider || !(this.packDays > 0)) return;
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.slider.value = String(Math.floor((Number(this.quantity.value) || 1) * this.packDays));
      this.slider.addEventListener('input',() => {
        const limits = this.limits();
        if (this.form.getAttribute('aria-busy') === 'true') return;
        const quantity = BmDaysPlanner.quantityForDays(Number(this.slider.value),this.packDays,limits.minimum,limits.increment,limits.maximum);
        if (quantity === null) return;
        this.quantity.value = String(quantity);
        this.quantity.dispatchEvent(new Event('input',{bubbles:true}));
        this.quantity.dispatchEvent(new Event('change',{bubbles:true}));
        this.queueSync();
      },options);
      this.form.addEventListener('input',event => {
        if (event.target === this.quantity) this.queueSync();
      },options);
      this.form.addEventListener('change',event => {
        if (event.target.matches('[name="quantity"],[data-bm-variant],[name="selling_plan"]')) this.queueSync();
      },options);
      this.observer = new MutationObserver(() => this.queueSync());
      this.observer.observe(this.quantity,{attributes:true,attributeFilter:['min','max','step','disabled']});
      this.observer.observe(this.form,{attributes:true,attributeFilter:['aria-busy']});
      const total = this.offers.querySelector('[data-bm-offers-selected-total]');
      if (total) this.observer.observe(total,{childList:true,subtree:true,characterData:true});
      this.sync();
    }
    limits() {
      const minimum = Math.max(1,Number(this.quantity.min) || 1);
      const increment = Math.max(1,Number(this.quantity.step) || 1);
      const stockLimit = this.quantity.max === '' ? 6 : Number(this.quantity.max);
      const cap = Math.min(6,Number.isFinite(stockLimit) ? stockLimit : 6);
      const maximum = minimum + Math.floor((cap - minimum) / increment) * increment;
      return {minimum,increment,maximum};
    }
    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      queueMicrotask(() => {
        this.syncQueued = false;
        if (this.isConnected) this.sync();
      });
    }
    setText(selector,text) {
      const element = this.querySelector(selector);
      if (element && element.textContent !== text) element.textContent = text;
    }
    sync() {
      const limits = this.limits();
      const quantity = Number(this.quantity.value);
      const variantId = this.form.querySelector('[name="id"]')?.value;
      const variant = this.offers.variants.find(item => String(item.id) === variantId);
      const valid = variant?.available && Number.isSafeInteger(quantity) && limits.maximum >= limits.minimum && quantity >= limits.minimum && quantity <= limits.maximum && (quantity - limits.minimum) % limits.increment === 0;
      this.hidden = !valid;
      if (!valid) return;
      const maximumDays = Math.floor(limits.maximum * this.packDays);
      this.slider.max = String(maximumDays);
      const mappedQuantity = BmDaysPlanner.quantityForDays(Number(this.slider.value),this.packDays,limits.minimum,limits.increment,limits.maximum);
      if (mappedQuantity !== quantity) this.slider.value = String(Math.min(maximumDays,Math.floor(quantity * this.packDays)));
      const target = Number(this.slider.value);
      const coverage = quantity * this.packDays;
      const plan = this.form.querySelector('[name="selling_plan"]')?.value;
      const allocation = variant.selling_plan_allocations?.find(item => String(item.selling_plan_id || item.selling_plan?.id) === plan);
      const price = allocation?.price ?? variant.price;
      const eligible = this.offers.dataset.discountEligible === 'true' && !plan;
      const total = this.offers.constructor.offerTotal(price,quantity,eligible);
      const number = new Intl.NumberFormat('ro-RO',{maximumFractionDigits:1});
      const unitLabel = quantity === 1 ? this.offers.config.messages.oneItem : this.offers.message('items',String(quantity));
      this.setText('[data-bm-days-target]',number.format(target));
      this.setText('[data-bm-days-maximum]',`${number.format(maximumDays)} zile`);
      this.setText('[data-bm-days-units]',unitLabel);
      this.setText('[data-bm-days-coverage]',`Acoperă ${number.format(coverage)} zile la porția de pe etichetă`);
      this.setText('[data-bm-days-unit-price]',`${this.offers.money(Math.round(total / coverage))} / zi`);
      this.slider.setAttribute('aria-valuetext',`${target} zile dorite. ${unitLabel}, pentru ${number.format(coverage)} zile.`);
      this.slider.disabled = this.form.getAttribute('aria-busy') === 'true';
      const progress = maximumDays > 1 ? (target - 1) / (maximumDays - 1) * 100 : 0;
      this.slider.style.setProperty('--days-progress',`${progress}%`);
    }
  }
  customElements.define('bm-days-planner',BmDaysPlanner);
}
